import { defineStore } from "pinia";
import { SalesReport } from "../models/SalesReport";
import { ComponentsApi } from "../api/ComponentsApi";
import _cloneDeep from "lodash/cloneDeep";
import { useUserManagerStore } from "./userManagerStore";
import cloneDeep from "lodash/cloneDeep";
import { useAppSettingsStore } from "./AppSettingsStore";
import { Preferences } from "@capacitor/preferences";

interface CenterWithSalesReports {
    centerId: number
    salesReports: SalesReport[]
    disconnected: boolean
}

export interface SalesReportsState {
    salesReportsByCenter: CenterWithSalesReports[]
    activeCenter: number | null
    showNewSalesReportsDialog: boolean
    newComponent: SalesReport
    newComponentPage: number
    editingIndex: number | null
    pages: Array<{ label: string, component: any }>
    deletingIndex: number | null
    updateIndex: number | null
    draggableKey: number
    lastUpdated: Date | null
    reportsToFetch: string[]
    disconnectedCenters: Set<number>
}

const salesReport = new SalesReport()

export const useSalesReportsStore = defineStore('salesReports', {
    state: (): SalesReportsState => ({
        salesReportsByCenter: [],
        activeCenter: null,
        showNewSalesReportsDialog: false,
        newComponent: salesReport,
        newComponentPage: -1,
        editingIndex: null,
        pages: [],
        updateIndex: null,
        deletingIndex: null,
        draggableKey: 0,
        lastUpdated: null,
        reportsToFetch: [],
        disconnectedCenters: new Set()
    }),

    getters: {
        salesReports: function (state) {
            return state.salesReportsByCenter.find((center) => center.centerId === state.activeCenter)?.salesReports
        },

        center: function (state) {
            return state.salesReportsByCenter.find((center) => center.centerId === state.activeCenter)
        },

        salesReportsForDisplayedCenters: function (state) {
            const userManagerStore = useUserManagerStore()
            const displayedCenters = userManagerStore.displayedCenters
            const salesReportsByCenter = state.salesReportsByCenter

            return displayedCenters.map((dc) => {
                return salesReportsByCenter.find((c) => dc.id == c.centerId)
            })
        }
    },

    actions: {
        toggleSalesReportsDialog: function (visible?: boolean) {
            const originalState = this.showNewSalesReportsDialog
            if (visible !== undefined) {
                this.showNewSalesReportsDialog = visible
            } else {
                this.showNewSalesReportsDialog = !this.showNewSalesReportsDialog
            }

            // Open the dialog if it was closed
            if (visible === true && originalState === false) {
                this.openSalesReportsDialog()
            }

            // Close the dialog if it was opened
            if (visible === false && originalState === true) {
                this.closeSalesReportsDialog()
            }
        },

        openSalesReportsDialog: function () {
            this.showNewSalesReportsDialog = true
            this.startNewSalesReport()
            this.newComponentPage = 0
        },

        closeSalesReportsDialog: function () {
            this.showNewSalesReportsDialog = false
            this.startNewSalesReport()
            this.newComponentPage = -1
            this.setEditingIndex(null)
        },

        startNewSalesReport: function () {
            this.newComponent = new SalesReport()
            this.newComponentPage = 0
        },

        setPages: function (pages: Array<{ label: string, component: any }>) {
            this.pages = pages
        },

        initCenterComponents: async function () {
            const userManagerStore = useUserManagerStore()
            const appSettings = useAppSettingsStore()
            const userComponents = userManagerStore.user?.components

            // User has no components
            if (!userComponents) {
                return
            }

            const salesReports: SalesReport[] = []

            const components = userComponents.map((component) => {
                let sr = new SalesReport()
                sr.updateFromJson(component.config)
                sr.id = component.id
                sr.taxIncluded = appSettings.getSettings.taxIncluded
                salesReports.push(sr)
                return sr
            })

            let inserted = false
            userManagerStore.user?.centers?.forEach((center) => {
                const c = this.salesReportsByCenter.find((c) => c.centerId === center.id)
                if (!c) {
                    this.salesReportsByCenter.push({ centerId: center.id, salesReports: cloneDeep(components), disconnected: false })
                    inserted = true
                } else {
                    // Sync: add new components, remove deleted ones, keep existing (preserves shifted dates/data)
                    const componentIds = new Set(components.map(comp => comp.id))
                    // Remove deleted components
                    c.salesReports = c.salesReports.filter(sr => componentIds.has(sr.id))
                    // Add new components only
                    components.forEach(comp => {
                        if (!c.salesReports.find(sr => sr.id === comp.id)) {
                            c.salesReports.push(cloneDeep(comp))
                        }
                    })
                }
            })

            // Sync disconnection state with backend
            userManagerStore.user?.centers?.forEach((center) => {
                if (center.connection_error) {
                    this.markCenterAsDisconnected(center.id)
                } else {
                    this.markCenterAsConnected(center.id)
                }
            })

            if (inserted) {
                this.salesReportsByCenter.sort((a, b) => a.centerId - b.centerId)
            }

            this.salesReportsByCenter.forEach(async (c) => {
                const center = userManagerStore.user?.centers?.find((center) => center.id === c.centerId)

                let storedCentersJson = await Preferences.get({ key: 'centers' })
                let storedCenters = JSON.parse(storedCentersJson.value!)
                let storedCenter = storedCenters.find((c: any) => c.id == center?.id)

                c.salesReports.forEach((sr) => {
                    sr.updateLanguageAndCurrency(center?.language!, center?.currencyCode!)
                    sr.setGmt(storedCenter?.gmt)
                })
            })

            useAppSettingsStore().setDataFetched(true)
        },

        addToRefreshList: function (componentId: string) {
            if (!this.reportsToFetch.includes(componentId)) {
                this.reportsToFetch.push(componentId)
            }
        },

        refreshCentersComponents: async function (skipFetch: boolean = false) {
            if (this.reportsToFetch.length === 0) {
                return
            }

            const userManagerStore = useUserManagerStore()
            const appSettings = useAppSettingsStore()
            const userComponents = userManagerStore.user?.components

            // User has no components
            if (!userComponents) {
                return
            }

            // Filter the components that are in the reportsToFetch list
            const salesReports: SalesReport[] = []
            const components = userComponents.filter((component) => this.reportsToFetch.includes(component.id)).map((component) => {
                let sr = new SalesReport()
                sr.updateFromJson(component.config)
                sr.id = component.id
                sr.taxIncluded = appSettings.getSettings.taxIncluded
                salesReports.push(sr)
                return sr
            })

            let inserted = false
            userManagerStore.user?.centers?.forEach((center) => {
                const c = this.salesReportsByCenter.find((c) => c.centerId === center.id)
                if (!c) {
                    this.salesReportsByCenter.push({ centerId: center.id, salesReports: cloneDeep(components), disconnected: false })
                    inserted = true
                } else {
                    // c.salesReports = cloneDeep(components)
                    components.forEach((component) => {
                        const index = c.salesReports.findIndex((sr) => sr.id === component.id)
                        if (index !== -1) {
                            const preservedOffset = c.salesReports[index].periodOffset
                            c.salesReports[index] = cloneDeep(component)
                            c.salesReports[index].periodOffset = preservedOffset
                            if (!skipFetch) {
                                c.salesReports[index].fetchAll(center.id, true)
                            }
                            c.salesReports[index].language = center.language
                            c.salesReports[index].currency = center.currencyCode
                        }
                    })

                }
            })

            // Sync disconnection state with backend
            userManagerStore.user?.centers?.forEach((center) => {
                if (center.connection_error) {
                    this.markCenterAsDisconnected(center.id)
                } else {
                    this.markCenterAsConnected(center.id)
                }
            })

            if (inserted) {
                this.salesReportsByCenter.sort((a, b) => a.centerId - b.centerId)
            }

            useAppSettingsStore().setDataFetched(true)
            this.reportsToFetch = []
        },

        initCenterComponent: async function (componentIndex: number) {
            const userManagerStore = useUserManagerStore();
            const userComponents = userManagerStore.user?.components
            const appSettings = useAppSettingsStore()

            if (!userComponents) {
                return
            }

            let config = userComponents[componentIndex].config;
            let sr = new SalesReport()
            sr.updateFromJson(config)
            sr.id = userComponents[componentIndex].id
            sr.taxIncluded = appSettings.getSettings.taxIncluded

            this.salesReportsByCenter.forEach(async (center) => {
                const report = cloneDeep(sr)
                await report.fetchAll(center.centerId)
                center.salesReports.push(sr)
            })

            this.lastUpdated = new Date()
        },

        fetchAllSalesReports: async function (force?: boolean) {
            this.salesReportsByCenter.forEach(async (center) => {
                center.salesReports.forEach(async (sr) => {
                    await sr.fetchAll(center.centerId, force)
                })
            })
            this.lastUpdated = new Date()
        },

        fetchSalesReportsDatas: async function () {
            this.salesReports?.map(async (sr) => {
                await sr.fetchAll(this.activeCenter ?? 0)
            })
            this.lastUpdated = new Date()
        },

        fetchSalesReportData: async function (srId: string, force?: boolean) {
            this.salesReportsByCenter.forEach(async (center) => {
                center.salesReports.forEach(async (sr) => {
                    if (sr.id === srId) {
                        await sr.fetchAll(center.centerId, force)
                    }
                })
            })
            this.lastUpdated = new Date()
        },

        setEditingIndex: function (index: number | null) {
            this.editingIndex = index
        },

        startUpdatingReport: function (index: number) {
            this.updateIndex = index
            this.showNewSalesReportsDialog = true
            this.newComponentPage = 0

            if (!this.salesReports) {
                throw new Error('report_not_found')
            }

            this.newComponent.updateFromJson(this.salesReports[index].toJSON())
            this.newComponent.data = _cloneDeep(this.salesReports[index].data)
        },

        setUpdateReportModal: function (index: number | null) {
            this.updateIndex = index
        },

        openDeleteModal: async function (index: number | null) {
            this.deletingIndex = index
        },

        deleteReport: async function (index: string): Promise<boolean> {

            const resp = await ComponentsApi.deleteComponent(index).catch((error) => error)

            if (resp instanceof Error) {
                return false
            }

            const userManagerStore = useUserManagerStore();
            userManagerStore.removeComponent(index)

            // Remove from salesReportsByCenter so the UI updates immediately
            this.salesReportsByCenter.forEach((center) => {
                const idx = center.salesReports.findIndex((sr) => sr.id === index)
                if (idx !== -1) {
                    center.salesReports.splice(idx, 1)
                }
            })

            return true
        },

        duplicateReport: async function (index: number) {
            const nComponent = new SalesReport()
            if (!this.salesReports) {
                throw new Error('report_not_found')
            }

            nComponent.updateFromJson(this.salesReports[index].toJSON())
            nComponent.data = _cloneDeep(this.salesReports[index].data)
            this.salesReports.push(nComponent)
            this.salesReports[this.salesReports.length - 1]

            await ComponentsApi.duplicateComponent(this.salesReports[index].id)
                .catch((error) => error)
        },

        setActiveCenter: function (centerId: number) {
            this.activeCenter = centerId
        },

        getReportForCenterAndComponent: function (centerId: number, componentId: string) {

            const center = this.salesReportsForDisplayedCenters.find((c) => c?.centerId === centerId)
            return center?.salesReports.find((sr) => sr.id === componentId)
        },

        updateTaxDisplay: function () {
            const appSettings = useAppSettingsStore();

            this.salesReports?.forEach((sr) => {
                sr.taxIncluded = appSettings.getSettings.taxIncluded
            })
        },

        markCenterAsDisconnected: function (centerId: number) {
            const center = this.salesReportsByCenter.find(c => c.centerId === centerId)
            if (center) {
                center.disconnected = true
                center.salesReports.forEach(sr => {
                    sr.markAsDisconnected()
                })
            }
            this.disconnectedCenters.add(centerId)
        },

        markCenterAsConnected: function (centerId: number) {
            const center = this.salesReportsByCenter.find(c => c.centerId === centerId)
            if (center) {
                center.disconnected = false
                center.salesReports.forEach(sr => {
                    sr.markAsConnected()
                })
            }
            this.disconnectedCenters.delete(centerId)
        },

        isCenterDisconnected: function (centerId: number): boolean {
            return this.disconnectedCenters.has(centerId)
        },

        /**
         * True when the center has reports AND every single one is in server error state.
         */
        allReportsServerError: function (centerId: number): boolean {
            const center = this.salesReportsByCenter.find(c => c.centerId === centerId)
            if (!center || center.salesReports.length === 0) return false
            return center.salesReports.every(sr => sr.serverError)
        },

        reconnectCenter: async function (centerId: number) {
            this.markCenterAsConnected(centerId)
            const center = this.salesReportsByCenter.find(c => c.centerId === centerId)
            if (center) {
                for (const sr of center.salesReports) {
                    try {
                        await sr.fetchAll(centerId, true)
                    } catch (error: any) {
                        console.error(`Failed to reconnect sales report ${sr.id} for center ${centerId}:`, error)
                        if (error.response?.status === 401) {
                            this.markCenterAsDisconnected(centerId)
                            break
                        }
                    }
                }
            }
        }
    }
})